import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useSearch } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetNoteContext,
  useCreateNote,
  getGetNoteContextQueryKey,
  type Note,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquarePlus, X, Check } from "lucide-react";
import {
  cssPath,
  currentPath,
  entityAnchorOf,
  highlight,
  locate,
  nearestHeading,
  noteKey,
  pinRectFor,
  type PinRect,
} from "@/lib/notes-core";

/**
 * Widget de notas ancladas. Se monta UNA vez, en el Layout, y solo para
 * sesiones que entraron por "Login with Amazify" (lo decide el Layout; el
 * servidor lo vuelve a comprobar en cada endpoint).
 *
 * Cinco responsabilidades, en este orden: lanzador, modo seleccion, redaccion,
 * pines y enlace profundo. La logica de DOM esta toda en lib/notes-core.ts.
 *
 * TODOS los nodos propios llevan `data-note-widget`. Sirve para dos cosas: que
 * el modo seleccion ignore los clicks sobre el widget, y que sus elementos no
 * cuenten al construir ni al resolver rutas estructurales (si contasen, abrir
 * el widget desplazaria los indices `nth-of-type` y las notas ya guardadas
 * resolverian a otro elemento).
 */

/** Cuanto se insiste en encontrar el objetivo de un enlace profundo. */
const DEEP_LINK_INTENTOS = 12;
const DEEP_LINK_ESPERA_MS = 120;

interface Compose {
  x: number;
  y: number;
  entityType: string | null;
  entityId: string | null;
  entityLabel: string | null;
  selector: string | null;
  contextLabel: string | null;
  targetLabel: string;
}

interface Pin {
  key: string;
  rect: PinRect;
  notes: Note[];
}

export function NoteWidget() {
  const [location] = useLocation();
  const search = useSearch();
  const queryClient = useQueryClient();

  const path = currentPath();
  const deepLinkId = new URLSearchParams(search).get("note") ?? undefined;

  const paramsContexto = { path, ...(deepLinkId ? { note: deepLinkId } : {}) };
  const { data: context } = useGetNoteContext(paramsContexto, {
    // La clave lleva dentro path y note, asi que navegar refresca el contexto
    // solo: no hace falta invalidar nada al cambiar de pagina.
    query: { queryKey: getGetNoteContextQueryKey(paramsContexto), staleTime: 15_000 },
  });

  const [seleccionando, setSeleccionando] = useState(false);
  const [resaltado, setResaltado] = useState<DOMRect | null>(null);
  const [compose, setCompose] = useState<Compose | null>(null);
  const [texto, setTexto] = useState("");
  const [pines, setPines] = useState<Pin[]>([]);
  const [abierto, setAbierto] = useState<string | null>(null);
  const [flotante, setFlotante] = useState<Note | null>(null);

  const crear = useCreateNote({
    mutation: {
      onSuccess: () => {
        setCompose(null);
        setTexto("");
        queryClient.invalidateQueries({ queryKey: getGetNoteContextQueryKey() });
      },
    },
  });

  const notasDePagina = useMemo(() => context?.page_notes ?? [], [context]);

  // ── Pines ──────────────────────────────────────────────────────────────────

  const recalcularPines = useCallback(() => {
    const porClave = new Map<string, Note[]>();
    for (const n of notasDePagina) {
      const k = noteKey(n);
      if (!k) continue; // de pagina: no tiene elemento donde pincharse
      porClave.set(k, [...(porClave.get(k) ?? []), n]);
    }
    const siguientes: Pin[] = [];
    for (const [key, notes] of porClave) {
      const el = locate(key);
      if (!el) continue;
      const rect = pinRectFor(el);
      // Sin tamaño = esta en otra pestaña o en otra pagina de la tabla. Un pin
      // en la esquina superior izquierda seria peor que ninguno.
      if (!rect) continue;
      siguientes.push({ key, rect, notes });
    }
    setPines(siguientes);
  }, [notasDePagina]);

  useEffect(() => {
    // Estrangulado con rAF: scroll y resize disparan decenas de eventos por
    // segundo y cada uno recorre el DOM llamando a getBoundingClientRect.
    let pendiente = 0;
    const programar = () => {
      if (pendiente) return;
      pendiente = window.requestAnimationFrame(() => {
        pendiente = 0;
        recalcularPines();
      });
    };
    programar();
    window.addEventListener("scroll", programar, true);
    window.addEventListener("resize", programar);
    // Un margen tras la navegacion: la pagina nueva todavia se esta pintando.
    const t = window.setTimeout(programar, 300);
    return () => {
      window.removeEventListener("scroll", programar, true);
      window.removeEventListener("resize", programar);
      window.clearTimeout(t);
      if (pendiente) window.cancelAnimationFrame(pendiente);
    };
  }, [recalcularPines, location]);

  // ── Modo seleccion ─────────────────────────────────────────────────────────

  const esDelWidget = (el: Element | null): boolean => !!el?.closest("[data-note-widget]");

  useEffect(() => {
    if (!seleccionando) return;

    const mover = (e: MouseEvent) => {
      const bajo = document.elementFromPoint(e.clientX, e.clientY);
      if (!bajo || esDelWidget(bajo)) return setResaltado(null);
      // Los anclas de entidad "iman": si hay un [data-note] encima, se resalta
      // ese y no el <span> concreto que hay debajo del cursor.
      const objetivo = bajo.closest("[data-note]") ?? bajo;
      setResaltado(objetivo.getBoundingClientRect());
    };

    const pinchar = (e: MouseEvent) => {
      const bajo = document.elementFromPoint(e.clientX, e.clientY);
      if (!bajo || esDelWidget(bajo)) return;
      // En captura y cortando el evento: si no, la aplicacion de debajo tambien
      // lo procesaria y elegir una fila abriria su ficha.
      e.preventDefault();
      e.stopPropagation();

      const objetivo = (bajo.closest("[data-note]") ?? bajo) as Element;
      const entidad = entityAnchorOf(objetivo);
      setCompose({
        x: e.clientX,
        y: e.clientY,
        entityType: entidad?.type ?? null,
        entityId: entidad?.id ?? null,
        entityLabel: entidad?.label ?? null,
        selector: entidad ? null : cssPath(objetivo),
        contextLabel: entidad ? null : (nearestHeading(objetivo) ?? document.title),
        targetLabel:
          entidad?.label ??
          nearestHeading(objetivo) ??
          objetivo.tagName.toLowerCase(),
      });
      setSeleccionando(false);
      setResaltado(null);
    };

    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSeleccionando(false);
        setResaltado(null);
      }
    };

    document.addEventListener("mousemove", mover, true);
    document.addEventListener("click", pinchar, true);
    document.addEventListener("keydown", escape, true);
    return () => {
      document.removeEventListener("mousemove", mover, true);
      document.removeEventListener("click", pinchar, true);
      document.removeEventListener("keydown", escape, true);
    };
  }, [seleccionando]);

  // ── Enlace profundo (?note=…) ──────────────────────────────────────────────

  const yaSaltado = useRef<string | null>(null);

  useEffect(() => {
    if (!deepLinkId || yaSaltado.current === deepLinkId) return;
    const nota =
      context?.deep_link_note ?? notasDePagina.find((n) => n.id === deepLinkId) ?? null;
    if (!nota) return;
    yaSaltado.current = deepLinkId;

    const key = noteKey(nota);
    if (!key) {
      // Nota de pagina: no hay elemento al que saltar, se enseña flotando.
      setFlotante(nota);
      return;
    }

    let intentos = 0;
    const buscar = () => {
      const el = locate(key) as HTMLElement | null;
      if (el) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        highlight(el);
        recalcularPines();
        setAbierto(key);
        return;
      }
      // En una carga en frio la fila puede no estar pintada todavia. Se insiste
      // un poco y, si aun asi no aparece, se enseña flotando: un enlace de la
      // bandeja no puede quedarse en nada.
      if (++intentos < DEEP_LINK_INTENTOS) window.setTimeout(buscar, DEEP_LINK_ESPERA_MS);
      else setFlotante(nota);
    };
    buscar();
  }, [deepLinkId, context, notasDePagina, recalcularPines]);

  // ── Pintado ────────────────────────────────────────────────────────────────

  const abiertoPin = pines.find((p) => p.key === abierto) ?? null;

  const enviar = () => {
    if (!compose || texto.trim() === "") return;
    crear.mutate({
      data: {
        body: texto.trim(),
        path,
        ...(compose.entityType && compose.entityId
          ? {
              entity_type: compose.entityType,
              entity_id: compose.entityId,
              ...(compose.entityLabel ? { entity_label: compose.entityLabel } : {}),
            }
          : {}),
        ...(compose.selector ? { selector: compose.selector } : {}),
        ...(compose.contextLabel ? { context_label: compose.contextLabel } : {}),
      },
    });
  };

  return createPortal(
    <div data-note-widget="">
      {/* Capa de resaltado del modo seleccion. `pointer-events: none` para que
          el raton siga llegando a la aplicacion de debajo. */}
      {seleccionando && resaltado && (
        <div
          className="fixed z-[9998] pointer-events-none border-2 border-primary bg-primary/10 rounded"
          style={{
            top: resaltado.top,
            left: resaltado.left,
            width: resaltado.width,
            height: resaltado.height,
          }}
        />
      )}

      {/* Pines */}
      {!seleccionando &&
        pines.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setAbierto(abierto === p.key ? null : p.key)}
            className="fixed z-[9997] h-5 min-w-5 px-1 rounded-full bg-primary text-primary-foreground text-[11px] font-semibold leading-5 shadow"
            style={{ top: p.rect.top, left: p.rect.left }}
            aria-label={`${p.notes.length} note${p.notes.length === 1 ? "" : "s"} here`}
          >
            {p.notes.length}
          </button>
        ))}

      {/* Tarjeta del pin abierto */}
      {abiertoPin && (
        <div
          className="fixed z-[9999] w-80 max-h-80 overflow-auto rounded-lg border bg-card p-3 shadow-lg"
          style={{
            top: Math.min(abiertoPin.rect.top + 18, window.innerHeight - 340),
            left: Math.min(abiertoPin.rect.left - 300, window.innerWidth - 330),
          }}
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <span className="text-xs font-medium text-muted-foreground">
              {abiertoPin.notes.length} open note
              {abiertoPin.notes.length === 1 ? "" : "s"}
            </span>
            <button type="button" onClick={() => setAbierto(null)} aria-label="Close">
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
          <ul className="space-y-3">
            {abiertoPin.notes.map((n) => (
              <li key={n.id} className="text-sm">
                <p className="whitespace-pre-wrap">{n.body}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {n.author?.name ?? "—"}
                  {n.comments.length > 0 && ` · ${n.comments.length} repl${n.comments.length === 1 ? "y" : "ies"}`}
                  {" · "}
                  <Link href={`/notes?note=${n.id}`} className="underline">
                    open thread
                  </Link>
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Tarjeta flotante: el enlace profundo no encontro su elemento (o la nota
          es de pagina). Nunca se deja un enlace muerto. */}
      {flotante && (
        <div className="fixed z-[9999] bottom-24 right-6 w-80 rounded-lg border bg-card p-3 shadow-lg">
          <div className="flex items-start justify-between gap-2 mb-2">
            <span className="text-xs font-medium text-muted-foreground">
              {flotante.context_label ?? flotante.entity_label ?? "Note"}
            </span>
            <button type="button" onClick={() => setFlotante(null)} aria-label="Close">
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
          <p className="text-sm whitespace-pre-wrap">{flotante.body}</p>
          <Link href={`/notes?note=${flotante.id}`} className="mt-2 block text-xs underline">
            open thread
          </Link>
        </div>
      )}

      {/* Redaccion */}
      {compose && (
        <div
          className="fixed z-[9999] w-80 rounded-lg border bg-card p-3 shadow-lg"
          style={{
            top: Math.min(compose.y + 12, window.innerHeight - 260),
            left: Math.min(compose.x, window.innerWidth - 340),
          }}
        >
          <p className="text-xs text-muted-foreground mb-2 truncate" title={compose.targetLabel}>
            {compose.entityId ? "On " : "Near "}
            <span className="font-medium">{compose.targetLabel}</span>
          </p>
          <Textarea
            autoFocus
            rows={4}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="What's wrong here?"
            className="text-sm"
          />
          <div className="mt-2 flex justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setCompose(null);
                setTexto("");
              }}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={enviar} disabled={crear.isPending || texto.trim() === ""}>
              {crear.isPending ? "Saving…" : "Save note"}
            </Button>
          </div>
        </div>
      )}

      {/* Lanzador */}
      <div className="fixed z-[9996] bottom-6 right-6 flex items-center gap-2">
        {(context?.open_count ?? 0) > 0 && (
          <Link
            href="/notes"
            className="rounded-full border bg-card px-3 py-1.5 text-xs shadow hover:bg-accent"
          >
            {context!.open_count} open
          </Link>
        )}
        <Button
          size="sm"
          variant={seleccionando ? "secondary" : "default"}
          className="rounded-full shadow"
          onClick={() => {
            setSeleccionando((v) => !v);
            setCompose(null);
            setAbierto(null);
          }}
        >
          {seleccionando ? (
            <>
              <Check className="mr-1 h-4 w-4" /> Pick an element… (Esc)
            </>
          ) : (
            <>
              <MessageSquarePlus className="mr-1 h-4 w-4" /> Add note
            </>
          )}
        </Button>
      </div>
    </div>,
    document.body,
  );
}
